function responseOk(body) {
  if (!body || typeof body !== "object") return false;
  if (Number(body.status) === 200) return true;
  if (body.success === true) return true;
  if (body.code === 0 || body.code === "0") return true;
  return false;
}

function extractPortfolioCurrentBalanceUsd(payload, body) {
  const raw = Number(
    payload?.currentBalanceUsd ??
      payload?.current_balance_usd ??
      payload?.balanceUsd ??
      payload?.balance_usd ??
      payload?.totalBalanceUsd ??
      payload?.total_balance_usd ??
      payload?.portfolioBalance ??
      payload?.portfolio_balance ??
      body?.currentBalanceUsd ??
      body?.current_balance_usd ??
      NaN,
  );
  return Number.isFinite(raw) ? raw : null;
}

/**
 * Pagination size shared by Portfolio wallet list + WalletTable fallback.
 */
export const WALLET_LIST_PAGE_SIZE = 10;

export const TRANSACTION_HISTORY_PAGE_SIZE = 10;

/**
 * Normalizes deposit/withdraw history list API into table + pagination state.
 */
export function normalizeTransactionHistoryResponse(body) {
  if (!body || typeof body !== "object") {
    return { rows: [], totalCount: 0, currentPage: 1, totalPages: 0 };
  }
  const data = body.data !== undefined ? body.data : body;
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const count = Number(data?.count);
  const totalCount = Number.isFinite(count) && count >= 0 ? count : rows.length;
  const currentPage = Number(data?.currentPage) >= 1 ? Number(data.currentPage) : 1;
  const totalPages = Number(data?.totalPages) >= 0 ? Number(data.totalPages) : 0;
  return { rows, totalCount, currentPage, totalPages };
}

/**
 * Normalizes `/get_wallet_list` payloads into `{ rows, totalCount }`.
 */
export function normalizeWalletListResponse(body) {
  if (!body || typeof body !== "object") {
    return { rows: [], totalCount: 0 };
  }
  const data = body.data !== undefined ? body.data : body;
  let rows = Array.isArray(data?.wallet_list)
    ? data.wallet_list
    : Array.isArray(data?.walletList)
      ? data.walletList
      : Array.isArray(data?.wallets)
        ? data.wallets
        : Array.isArray(data?.list)
          ? data.list
          : Array.isArray(data?.rows)
            ? data.rows
            : Array.isArray(data)
              ? data
              : [];

  if (
    !rows.length &&
    data &&
    typeof data === "object" &&
    !Array.isArray(data)
  ) {
    const keyed = Object.values(data).filter(
      (item) =>
        item &&
        typeof item === "object" &&
        (item.walletId != null ||
          item.wallet_id != null ||
          item.walletName != null ||
          item.wallet_name != null),
    );
    if (keyed.length) rows = keyed;
  }
  const tc = Number(
    data?.total_count ??
      data?.totalCount ??
      data?.count ??
      body?.total_count ??
      body?.totalCount,
  );
  const totalCount =
    Number.isFinite(tc) && tc >= 0 ? Math.floor(tc) : rows.length;
  return { rows, totalCount };
}

/**
 * Consistent USD display for portfolio headings, charts, wallet cells.
 */
export function formatPortfolioUsdAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return "—";
  }
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

/** Rolling window lengths (days inclusive ending today). */
export const PORTFOLIO_CHART_RANGE_DAYS = {
  "1m": 30,
  "3m": 90,
  "6m": 180,
  "1y": 365,
};

function startOfLocalDay(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return x;
}

function toYyyyMmDd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getRollingRangeStartDate(dayCount) {
  const end = startOfLocalDay(new Date());
  const start = new Date(end);
  start.setDate(start.getDate() - (dayCount - 1));
  return start;
}

function labelsValuesToPoints(labels, values) {
  const points = [];
  for (let i = 0; i < labels.length; i += 1) {
    const balance = Number(values[i]);
    if (!Number.isFinite(balance)) continue;
    points.push({
      date: String(labels[i]),
      balance,
      isVirtual: false,
    });
  }
  return sortPortfolioGraphPointsByDate(points);
}

function sortPortfolioGraphPointsByDate(points) {
  return [...points].sort((a, b) => {
    const da = parseChartDateLabel(a.date);
    const db = parseChartDateLabel(b.date);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da.getTime() - db.getTime();
  });
}

/**
 * Frontend filter: rolling window from today, or full series for All.
 */
export function filterPortfolioGraphPoints(points, range) {
  if (!Array.isArray(points) || !points.length) return [];
  const sorted = sortPortfolioGraphPointsByDate(points);
  if (range === "All") return sorted;
  const dayCount = PORTFOLIO_CHART_RANGE_DAYS[range];
  if (!dayCount) return sorted;
  const start = getRollingRangeStartDate(dayCount);
  const end = startOfLocalDay(new Date());
  return sorted.filter((p) => {
    const d = parseChartDateLabel(p.date);
    if (!d) return false;
    return d.getTime() >= start.getTime() && d.getTime() <= end.getTime();
  });
}

/**
 * When exactly one point remains after filtering, prepend a virtual point (not persisted).
 */
export function ensurePortfolioChartMinTwoPoints(points, range) {
  if (!Array.isArray(points) || points.length !== 1) {
    return points ?? [];
  }
  const real = { ...points[0], isVirtual: false };
  const balance = real.balance;
  let virtualDate;

  if (range === "All") {
    const d = parseChartDateLabel(real.date);
    if (!d) return [real];
    const prev = new Date(d);
    prev.setDate(prev.getDate() - 1);
    virtualDate = toYyyyMmDd(prev);
  } else {
    const dayCount = PORTFOLIO_CHART_RANGE_DAYS[range];
    if (!dayCount) return [real];
    virtualDate = toYyyyMmDd(getRollingRangeStartDate(dayCount));
  }

  return [
    { date: virtualDate, balance, isVirtual: true },
    real,
  ];
}

/**
 * Filter API series by range, then ensure ≥2 chart points (virtual pad when needed).
 */
export function applyPortfolioChartRange(labels, values, range) {
  const base = labelsValuesToPoints(labels, values);
  const filtered = filterPortfolioGraphPoints(base, range);
  const points = ensurePortfolioChartMinTwoPoints(filtered, range);
  return {
    labels: points.map((p) => p.date),
    values: points.map((p) => p.balance),
    points,
  };
}

/**
 * Normalizes portfolio balance graph API responses into Chart.js-friendly series.
 * Supports several common envelope and row field names.
 */
export function normalizePortfolioGraph(body) {
  if (!body) {
    return { labels: [], values: [], currentBalanceUsd: null };
  }

  const payload = body.data !== undefined ? body.data : body;
  const currentBalanceUsd = extractPortfolioCurrentBalanceUsd(payload, body);

  const explicitError =
    Number(body.status) >= 400 || body.success === false || body.error === true;
  if (explicitError) {
    return { labels: [], values: [], currentBalanceUsd: null };
  }

  if (
    Array.isArray(payload?.labels) &&
    Array.isArray(payload?.values) &&
    payload.labels.length > 0 &&
    payload.labels.length === payload.values.length
  ) {
    const labels = payload.labels.map((x) => String(x));
    const values = payload.values.map((v) => Number(v));
    return { labels, values, currentBalanceUsd };
  }

  const list = Array.isArray(payload)
    ? payload
    : payload?.points ??
      payload?.graph ??
      payload?.series ??
      payload?.dataPoints ??
      payload?.items ??
      payload?.list ??
      [];

  if (!Array.isArray(list) || list.length === 0) {
    return { labels: [], values: [], currentBalanceUsd };
  }

  const labels = [];
  const values = [];

  list.forEach((item, index) => {
    if (!item || typeof item !== "object") return;
    const rawLabel =
      item.label ?? item.date ?? item.period ?? item.month ?? item.time ?? item.timestamp;
    const v = Number(
      item.balanceUsd ??
        item.balance_usd ??
        item.value ??
        item.balance ??
        item.usd_value ??
        item.usdValue ??
        item.total ??
        item.portfolio_balance ??
        item.amount,
    );
    const labelStr =
      rawLabel !== undefined && rawLabel !== null && rawLabel !== ""
        ? String(rawLabel)
        : String(index + 1);

    if (Number.isFinite(v)) {
      labels.push(labelStr);
      values.push(v);
    }
  });

  return { labels, values, currentBalanceUsd };
}

export function parseChartDateLabel(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const day = Number(m[3]);
  const d = new Date(y, mo, day);
  if (d.getFullYear() !== y || d.getMonth() !== mo || d.getDate() !== day) return null;
  return d;
}

function formatMonthBoundaryLabels(parsed) {
  let prevKey = "";
  return parsed.map((d) => {
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key === prevKey) return "";
    prevKey = key;
    return d.toLocaleString("en", { month: "short" });
  });
}

function formatMonthYearBoundaryLabels(parsed) {
  let prevKey = "";
  return parsed.map((d) => {
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key === prevKey) return "";
    prevKey = key;
    const m = d.toLocaleString("en", { month: "short" });
    const yy = String(d.getFullYear()).slice(-2);
    return `${m} '${yy}`;
  });
}

function formatAllRangeLabels(parsed) {
  if (parsed.length < 2) {
    return parsed.map((d) =>
      d.toLocaleString("en", { month: "short", day: "numeric", year: "2-digit" }),
    );
  }
  const daysApprox = (parsed[parsed.length - 1] - parsed[0]) / 86400000;
  if (daysApprox > 400) {
    let prevYear = null;
    return parsed.map((d) => {
      const y = d.getFullYear();
      if (y === prevYear) return "";
      prevYear = y;
      return String(y);
    });
  }
  if (daysApprox > 120) {
    return formatMonthBoundaryLabels(parsed);
  }
  return parsed.map((d) =>
    d.toLocaleString("en", { month: "short", day: "numeric" }),
  );
}

/**
 * X-axis labels for portfolio line chart: short form depends on selected range (1m / 3m / 6m / 1y / All).
 * Expects API labels like "2026-03-02" (YYYY-MM-DD).
 */
export function formatPortfolioChartAxisLabels(labels, range) {
  if (!Array.isArray(labels) || !labels.length) return labels;
  const parsed = labels.map(parseChartDateLabel);
  if (parsed.some((d) => !d)) {
    return [...labels];
  }

  switch (range) {
    case "1m":
      return parsed.map((d) =>
        d.toLocaleString("en", { month: "short", day: "numeric" }),
      );
    case "3m":
      return formatMonthBoundaryLabels(parsed);
    case "6m":
    case "1y":
      return formatMonthYearBoundaryLabels(parsed);
    case "All":
      return formatAllRangeLabels(parsed);
    default:
      return formatMonthYearBoundaryLabels(parsed);
  }
}

export function formatPortfolioTooltipTitle(rawLabel) {
  const d = parseChartDateLabel(rawLabel);
  if (!d) return String(rawLabel ?? "");
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Normalizes asset allocation API responses for the doughnut chart.
 */
export function normalizeAssetAllocation(body) {
  if (!body) {
    return { items: null, totalUsd: null };
  }

  const payload = body.data !== undefined ? body.data : body;
  const rawList = Array.isArray(payload)
    ? payload
    : payload?.allocation ?? payload?.assets ?? payload?.items ?? payload?.list ?? [];

  const explicitError =
    Number(body.status) >= 400 || body.success === false || body.error === true;
  if (explicitError) {
    return { items: null, totalUsd: null };
  }

  if (!responseOk(body) && (!Array.isArray(rawList) || rawList.length === 0)) {
    return { items: null, totalUsd: null };
  }

  let totalUsd = Number(
    payload?.totalBalanceUsd ??
      payload?.total_balance_usd ??
      payload?.availableFundsUsd ??
      payload?.available_funds_usd ??
      payload?.total_usd ??
      payload?.totalUsd ??
      payload?.finalAmount ??
      0,
  );

  const items = [];

  rawList.forEach((row) => {
    if (!row || typeof row !== "object") return;
    const asset_name =
      row.coinName ??
      row.coin_name ??
      row.coinSymbol ??
      row.coin_symbol ??
      row.asset_name ??
      row.assetName ??
      row.symbol ??
      row.name ??
      row.currency ??
      "Unknown";
    const usd_value = Number(
      row.valueUsd ??
        row.value_usd ??
        row.usd_value ??
        row.usdValue ??
        row.amount_usd ??
        row.amountUsd ??
        row.usd ??
        0,
    );
    const free = Number(row.balance ?? row.availableBalance ?? 0);
    const locked = Number(row.lockedBalance ?? row.locked_balance ?? 0);
    const tokenBalance = free + locked;
    const coinSymbol = row.coinSymbol ?? row.coin_symbol ?? "";
    const imagePathRaw = row.imagePath ?? row.image_path ?? "";
    const imagePath =
      typeof imagePathRaw === "string" && imagePathRaw.trim() ? imagePathRaw.trim() : "";

    items.push({
      asset_name,
      usd_value,
      tokenBalance,
      coinSymbol,
      imagePath,
    });
  });

  if ((!Number.isFinite(totalUsd) || totalUsd <= 0) && items.length) {
    totalUsd = items.reduce((s, x) => s + Number(x.usd_value || 0), 0);
  }

  return {
    items,
    totalUsd: Number.isFinite(totalUsd) ? totalUsd : null,
  };
}

/** API query for full history; range buttons filter on the client. */
export const PORTFOLIO_GRAPH_API_RANGE = "all";
